import { NotificationType, useTranslation } from "@next-platform/client-common-core";
import { apiRulebookPublishingClient } from "@next-platform/client-common-core/rulebook/editor/rulebookPublishingClient";
import type { RulebookSummary } from "@next-platform/packages-ts-types";
import { useCallback, useState } from "react";

import useToast from "@/hooks/useToast";

type UseSavedRulebookDeleteOptions = {
  summary?: RulebookSummary;
  onReloadSaved?: () => void;
};

/** Server delete + modal state for a saved Rulebook with no linked local draft. */
const useSavedRulebookDelete = ({ summary, onReloadSaved }: UseSavedRulebookDeleteOptions) => {
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const [stateIsOpen, setStateIsOpen] = useState(false);
  const [stateIsDeleting, setStateIsDeleting] = useState(false);

  const open = useCallback(() => setStateIsOpen(true), []);
  const close = useCallback(() => setStateIsOpen(false), []);
  const confirm = useCallback(async () => {
    if (!summary) {
      return;
    }
    setStateIsDeleting(true);
    try {
      await apiRulebookPublishingClient.deleteRulebook({ rulebookId: summary.id });
      onReloadSaved?.();
    } catch {
      displayToast({ type: NotificationType.error, message: t("rulebookEditor:card.deleteRulebookFailed") });
    } finally {
      setStateIsDeleting(false);
      setStateIsOpen(false);
    }
  }, [summary, onReloadSaved, displayToast, t]);

  return { isOpen: stateIsOpen, isDeleting: stateIsDeleting, open, close, confirm };
};

export default useSavedRulebookDelete;
